import { useUpdateGroupMutation } from '@/app/redux/endpoints/groups';
import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useAssessments } from '@/app/redux/hooks/assessments';
import { useGroups } from '@/app/redux/hooks/groups';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import {
  setSelectedGroupIds,
  setSelectedTab,
} from '@/app/redux/slices/insightsSlice';
import { setAttemptedProFeature } from '@/app/redux/slices/modalsSlice';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import {
  ContentPanelBody,
  ContentPanelCloseButton,
  ContentPanelCollapseButton,
  ContentPanelContainer,
  ContentPanelControls,
  ContentPanelHeader,
  ContentPanelLeft,
  ContentPanelRight,
  ContentPanelTabsTrigger,
  ContentPanelTitle,
} from '@/common/ui/ContentPanel';
import { FeatureSwitch, useIsFeatureEnabled } from '@/common/ui/FeatureGate';
import InitialsCircle from '@/common/ui/InitialsCircle';
import { ActionLoader } from '@/common/ui/LoadingSpinners';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { ProIcon } from '@/common/ui/UpgradeButton';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import AssessmentsTable from '@/dashboard/Assessment/AssessmentsTable';
import CreateAssessmentDialogue from '@/dashboard/Assessment/CreateAssessmentDialogue';
import {
  CaretDoubleLeftIcon,
  CaretDoubleRightIcon,
  CaretDownIcon,
  CaretUpIcon,
  ChartLineIcon,
  GearIcon,
  PencilLineIcon,
  PlusIcon,
  StudentIcon,
} from '@phosphor-icons/react';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import { Tabs, TabsContent, TabsList } from '@shadcn/components/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { Info } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import styled from 'styled-components';
import LearnerList from '../LearnerList/LearnerList';
import AddLearnersToGroupDialog from './AddLearnersToGroupDialog';
import GroupDetails from './GroupDetails';

export default function GroupView() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isFeatureEnabled = useIsFeatureEnabled();
  const { groupId, tab: activeTab } = useParams();

  const { students, isLoading, isFetching } = useStudents();
  const { organisation, currentOrganisationId } = useOrganisation();
  const { groups } = useGroups();

  const userId = useAppSelector(selectUserId);

  const [updateGroupMutation] = useUpdateGroupMutation();

  const [addLearnersDialogOpen, setAddLearnersDialogOpen] = useState(false);
  const [assessmentDialogueOpen, setAssessmentDialogueOpen] = useState(false);
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(true);

  const groupIds = groups.map((g) => g.id);
  const selectedGroup = groups.find((group) => group.id === groupId);
  const selectedGroupIdx = groupId ? groupIds.indexOf(groupId) : 0;
  const groupManagers = (organisation?.members ?? []).filter((m) =>
    selectedGroup?.managerIds.includes(m.uid),
  );

  const groupStudents = useMemo(() => {
    return (
      students?.filter((s) => selectedGroup?.studentIds.includes(s.id)) || []
    );
  }, [students, selectedGroup]);

  const { assessments: allAssessments, placementTests: allPlacementTests } =
    useAssessments();
  const studentIds = selectedGroup?.studentIds;
  const assessments = useMemo(
    () =>
      studentIds
        ? allAssessments.filter((a) =>
            Object.keys(a.students).some((sId) => studentIds.includes(sId)),
          )
        : [],
    [allAssessments, studentIds],
  );
  const placementTests = useMemo(
    () =>
      studentIds
        ? allPlacementTests.filter((t) => studentIds.includes(t.studentId))
        : [],
    [allPlacementTests, studentIds],
  );

  const createAssessmentsDisabledTooltip = useMemo(() => {
    // get the names of the first three students with assessments
    const names = groupStudents
      .filter((s) => !!s.assessmentId)
      .slice(0, 3)
      .map((s) => `${s.studentFirstName} ${s.studentSurname}`);

    // return a tooltip corresponding
    switch (names.length) {
      case 0:
        return null;
      case 1:
        return `${names[0]} already has an active assessment`;
      case 2:
        return `${names[0]} and ${names[1]} already have active assessments`;
      default:
        return `${names[0]}, ${names[1]} and others already have active assessments`;
    }
  }, [groupStudents]);

  function handleNavigateBack() {
    navigate('/dashboard/groups');
  }

  function handleNavigatePrevious() {
    if (selectedGroupIdx === 0) {
      return;
    }
    navigate(`/dashboard/groups/${groupIds[selectedGroupIdx - 1]}`);
  }

  function handleNavigateNext() {
    if (selectedGroupIdx === groupIds.length - 1) {
      return;
    }
    navigate(`/dashboard/groups/${groupIds[selectedGroupIdx + 1]}`);
  }

  const handleUpdateStudents = async (studentIds: string[]) => {
    if (!selectedGroup?.id || !currentOrganisationId) return;

    try {
      const updatedStudentIds = [
        ...new Set([...selectedGroup.studentIds, ...studentIds]),
      ];

      updateGroupMutation({
        orgId: currentOrganisationId,
        groupId: selectedGroup.id,
        updates: { studentIds: updatedStudentIds },
      });

      toast.success('Group learners updated');

      if (userId) {
        trackMixpanelEvent('Group learners updated', {
          organisationId: selectedGroup.organisationId,
          groupId: selectedGroup.id,
          studentIds: updatedStudentIds,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to update group learners', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  };

  function handleViewInsights() {
    if (!selectedGroup?.id) return;
    dispatch(setSelectedGroupIds([selectedGroup.id]));
    dispatch(setSelectedTab('groups'));

    trackMixpanelEvent('View Insights from Group View', {
      groupId: selectedGroup.id,
    });

    navigate('/dashboard/insights');
  }

  if (!selectedGroup) {
    return null;
  }
  return (
    <ContentPanelContainer>
      <ContentPanelHeader>
        <ContentPanelTitle>
          <InitialsCircle userName={selectedGroup.name} size={24} />
          {selectedGroup.name}
          <Badge className="px-1 text-xxs" variant="secondary">
            {isLoading || isFetching ? (
              <ActionLoader className="size-3" />
            ) : (
              groupStudents.length
            )}
          </Badge>
        </ContentPanelTitle>

        <ContentPanelControls>
          <div className="flex gap-1">
            <Button
              className="px-1 py-1 h-fit"
              variant="outline"
              onClick={handleNavigatePrevious}
              disabled={selectedGroupIdx === 0}
            >
              <CaretUpIcon />
            </Button>
            <Button
              className="px-1 py-1 h-fit"
              variant="outline"
              onClick={handleNavigateNext}
              disabled={selectedGroupIdx === groupIds.length - 1}
            >
              <CaretDownIcon />
            </Button>
          </div>

          <span className="text-muted-foreground pointer-events-none">
            Group {selectedGroupIdx + 1} of {groupIds.length}
          </span>

          <ContentPanelCloseButton onClick={handleNavigateBack} />
        </ContentPanelControls>
      </ContentPanelHeader>

      <ContentPanelBody>
        <ContentPanelLeft
          className={isRightPanelCollapsed ? 'flex-1' : undefined}
        >
          <Tabs
            defaultValue="learners"
            className="h-full w-full"
            value={activeTab}
            onValueChange={(value) => {
              navigate(`/dashboard/groups/${groupId}/${value}`);
            }}
          >
            <GroupContentHeader>
              <TabsList className="bg-white gap-1">
                <ContentPanelTabsTrigger value="learners" $withButtons>
                  <StudentIcon size={16} /> Learners
                </ContentPanelTabsTrigger>
                <ContentPanelTabsTrigger value="assessments" $withButtons>
                  <PencilLineIcon size={16} /> Assessments
                  {!isFeatureEnabled('assessments') && <ProIcon />}
                </ContentPanelTabsTrigger>
              </TabsList>

              <div
                className={`flex items-center gap-2 transition-all duration-200 ${
                  isRightPanelCollapsed ? 'pr-[124px]' : ''
                }`}
              >
                <FeatureSwitch
                  feature="assessments"
                  enabled={
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {/* wrap button in span so tooltip doesn't get disabled */}
                        <span>
                          <Button
                            variant="outline"
                            className="gap-2"
                            onClick={() => {
                              trackMixpanelEvent(
                                'Create Assessment clicked from Group View',
                              );
                              setAssessmentDialogueOpen(true);
                            }}
                            disabled={!!createAssessmentsDisabledTooltip}
                          >
                            <PencilLineIcon size={16} />
                            Create Assessment
                            {createAssessmentsDisabledTooltip && (
                              <Info color="hsl(var(--primary))" size={16} />
                            )}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {createAssessmentsDisabledTooltip && (
                        <TooltipContent>
                          {createAssessmentsDisabledTooltip}
                        </TooltipContent>
                      )}
                    </Tooltip>
                  }
                  disabled={
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={() =>
                        dispatch(setAttemptedProFeature('Assessments'))
                      }
                    >
                      <PencilLineIcon size={16} />
                      Create Assessment
                      <ProIcon />
                    </Button>
                  }
                />

                <FeatureSwitch
                  feature="insights"
                  enabled={
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={handleViewInsights}
                    >
                      <ChartLineIcon size={16} />
                      View Insights
                    </Button>
                  }
                  disabled={
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={() =>
                        dispatch(setAttemptedProFeature('Insights'))
                      }
                    >
                      <ChartLineIcon size={16} />
                      View Insights
                      <ProIcon />
                    </Button>
                  }
                />

                <Button
                  className="flex items-center gap-2"
                  onClick={() => setAddLearnersDialogOpen(true)}
                >
                  <PlusIcon size={16} color="hsl(var(--primary-foreground))" />
                  Add Learners
                </Button>
              </div>
            </GroupContentHeader>

            <GroupContentBody>
              <TabsContent value="learners" className="h-full">
                <LearnerList
                  students={groupStudents}
                  group={selectedGroup}
                  onAddLearnersToGroup={() => setAddLearnersDialogOpen(true)}
                />
              </TabsContent>
              <TabsContent value="assessments">
                <AssessmentsTable
                  assessments={assessments}
                  onCreateAssessment={() =>
                    isFeatureEnabled('assessments')
                      ? setAssessmentDialogueOpen(true)
                      : dispatch(setAttemptedProFeature('Assessments'))
                  }
                  placementTests={placementTests}
                  visibleStudentIds={selectedGroup.studentIds}
                />
              </TabsContent>
            </GroupContentBody>
          </Tabs>
        </ContentPanelLeft>

        <ContentPanelRight
          className={isRightPanelCollapsed ? 'collapsed' : undefined}
        >
          <Tabs
            defaultValue="details"
            value={isRightPanelCollapsed ? '' : 'details'}
            className="content-panel-child"
          >
            <GroupContentHeader>
              <TabsList className="bg-white gap-1">
                <ContentPanelCollapseButton
                  onClick={() =>
                    setIsRightPanelCollapsed(!isRightPanelCollapsed)
                  }
                  $isCollapsed={isRightPanelCollapsed}
                >
                  <ContentPanelTabsTrigger
                    value="details"
                    className="gap-2"
                    $withButtons
                  >
                    {!isRightPanelCollapsed && <CaretDoubleLeftIcon />}
                    <GearIcon size={18} /> Settings
                    {isRightPanelCollapsed && <CaretDoubleRightIcon />}
                  </ContentPanelTabsTrigger>
                </ContentPanelCollapseButton>
              </TabsList>
            </GroupContentHeader>

            <GroupDetailsBody>
              <TabsContent value="details">
                <GroupDetails group={selectedGroup} managers={groupManagers} />
              </TabsContent>
            </GroupDetailsBody>
          </Tabs>
        </ContentPanelRight>
      </ContentPanelBody>

      <AddLearnersToGroupDialog
        groupId={selectedGroup.id}
        open={addLearnersDialogOpen}
        onClose={() => setAddLearnersDialogOpen(false)}
        existingStudentIds={groupStudents.map((s) => s.id)}
        onSubmit={handleUpdateStudents}
      />

      <CreateAssessmentDialogue
        isOpen={assessmentDialogueOpen}
        onClose={() => setAssessmentDialogueOpen(false)}
        initialStudents={groupStudents.map((s) => s.id)}
      />
    </ContentPanelContainer>
  );
}

const GroupContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid hsl(var(--border));
  padding: 8px 16px;
`;

const GroupContentBody = styled.div`
  overflow-y: auto;
  height: 100%;
`;

const GroupDetailsBody = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;
